import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import PropTypes from "prop-types";

//Simple bar
import SimpleBar from "simplebar-react";

// MetisMenu
import MetisMenu from "metismenujs";
import { Link } from 'react-router-dom';
import { UncontrolledTooltip } from "reactstrap";

import { withTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import withRouter from "../../components/Common/withRouter";

import SidebarData, { GlobalSidebarData } from './SidebarData';
import SidebarOrgSwitcher from './SidebarOrgSwitcher';
import usePermission from '../../Hooks/usePermission';
import api from '../../helpers/api';

const SidebarContent = (props) => {

  const ref = useRef();
  const { hasPermission, isAdmin, isRequestor } = usePermission();
  const [pendingSRCount, setPendingSRCount] = useState(0);
  const [pendingProcurementCount, setPendingProcurementCount] = useState(0);
  const [pendingTransferCount, setPendingTransferCount] = useState(0);
  const activeContext = useSelector((s) => s.ActiveContext);
  const isGlobalView = !!activeContext?.isGlobalView;
  const isCollapsed = useSelector((s) => s.Layout?.leftSideBarType === "condensed");

  // Fetch pending service request count for admins. Skipped while in Global
  // View — service-request/all is org-scoped (@RequireActiveOrg) and would
  // 400 on every poll.
  useEffect(() => {
    if (!isAdmin || isGlobalView) return;
    const fetchPendingCount = async () => {
      try {
        const { data } = await api.get('service-request/all?status=PENDING');
        setPendingSRCount(data?.response?.length || 0);
      } catch (error) {
        console.error('Error fetching pending SR count:', error);
      }
    };
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, [isAdmin, isGlobalView]);

  // Fetch pending procurement request count for admins. Skipped in Global View
  // (procurement-request/pending-count is org-scoped via @RequireActiveOrg).
  useEffect(() => {
    if (!isAdmin || isGlobalView) return;
    const fetchPendingProcurement = async () => {
      try {
        const { data } = await api.get('procurement-request/pending-count');
        setPendingProcurementCount(data?.response?.count || 0);
      } catch (error) {
        console.error('Error fetching pending procurement count:', error);
      }
    };
    fetchPendingProcurement();
    const interval = setInterval(fetchPendingProcurement, 60000);
    return () => clearInterval(interval);
  }, [isAdmin, isGlobalView]);

  // Fetch the number of asset-transfer requests waiting on this organization's administrators
  // (its own pending requests plus incoming cross-organization transfers). It shares the Planning
  // badge with procurement. Same admin / Global View guard as the polls above, and it also
  // refreshes on demand when a transfer is approved, rejected or withdrawn elsewhere in the app,
  // so the badge drops the moment the admin acts instead of up to a minute later.
  useEffect(() => {
    if (!isAdmin || isGlobalView) return;
    const fetchPendingTransfers = async () => {
      try {
        const { data } = await api.get('equipment/asset-transfer/pending-count');
        setPendingTransferCount(data?.response?.count || 0);
      } catch (error) {
        console.error('Error fetching pending transfer count:', error);
      }
    };
    fetchPendingTransfers();
    const interval = setInterval(fetchPendingTransfers, 60000);
    window.addEventListener('transfers-updated', fetchPendingTransfers);
    return () => {
      clearInterval(interval);
      window.removeEventListener('transfers-updated', fetchPendingTransfers);
    };
  }, [isAdmin, isGlobalView]);

  // The Planning entry hosts both queues an admin has to clear.
  const pendingPlanningCount = pendingProcurementCount + pendingTransferCount;

  // When the user is in Global View but the current pathname targets an
  // org-scoped page (e.g. /settings, /reactive), redirect to the Global View
  // dashboard. Without this redirect the page would render its old content
  // briefly and side-effect requests would 400 against @RequireActiveOrg.
  useEffect(() => {
    if (!isGlobalView) return;
    const currentPath = props.router?.location?.pathname || window.location.pathname;
    const isGlobalRoute =
      currentPath.startsWith('/app/global/') ||
      currentPath === '/profile' ||
      currentPath === '/notifications' ||
      currentPath === '/logout';
    if (!isGlobalRoute) {
      props.router?.navigate?.('/app/global/dashboard');
    }
  }, [isGlobalView, props.router]);

  // Filter sidebar items based on user permissions
  const filteredSidebarData = useMemo(() => {
    // In Global View (active org is null): swap in the slim sidebar. Org-scoped
    // pages reject requests with no active org via @RequireActiveOrg, so we
    // hide them rather than show 400-on-click.
    if (isGlobalView) {
      return GlobalSidebarData(props.t);
    }

    // Requesters can only see: Service Requests, Assets
    const requesterAllowedLabels = ['service_requests', 'planning'];
    const requesterAllowedSubItems = ['assets'];

    return SidebarData(props.t).filter(item => {
      // Always show main menu labels
      if (item.isMainMenu) return true;

      // Requester-specific filtering - must be checked before locked items
      if (isRequestor) {
        // Hide locked items from requesters
        if (item.isLocked) return false;
        // Allow whitelisted top-level items
        if (requesterAllowedLabels.includes(item.id)) return true;
        // Allow Inventory parent (for Assets sub-item)
        if (item.id === 'inventory') return true;
        return false;
      }

      // Show locked items (feature previews) for non-requesters
      if (item.isLocked) return true;

      // If no permission required, show the item
      if (!item.permission && !item.permissions) return true;

      // Admins can see everything
      if (isAdmin) return true;

      // Check plural permissions (any match)
      if (item.permissions) {
        return item.permissions.some(p => hasPermission(p));
      }

      // Check if user has the required permission
      return hasPermission(item.permission);
    }).map(item => {
      // Also filter sub-items if they have permissions
      if (item.subItem) {
        const filteredSubItems = item.subItem.filter(subItem => {
          // Requester: only show Assets under Inventory
          if (isRequestor) {
            return requesterAllowedSubItems.includes(subItem.id);
          }
          if (!subItem.permission) return true;
          if (isAdmin) return true;
          return hasPermission(subItem.permission);
        });
        // Don't show parent if no sub-items remain
        if (filteredSubItems.length === 0) return null;
        return { ...item, subItem: filteredSubItems };
      }
      return item;
    }).filter(Boolean);
  }, [hasPermission, isAdmin, isRequestor, props.t, isGlobalView]);

  const activateParentDropdown = useCallback((item) => {
    item.classList.add("active");
    const parent = item.parentElement;
    const parent2El = parent.childNodes[1];

    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show");
    }

    if (parent) {
      parent.classList.add("mm-active");
      const parent2 = parent.parentElement;

      if (parent2) {
        parent2.classList.add("mm-show"); // ul tag

        const parent3 = parent2.parentElement; // li tag

        if (parent3) {
          parent3.classList.add("mm-active"); // li
          parent3.childNodes[0].classList.add("mm-active"); //a
          const parent4 = parent3.parentElement; // ul
          if (parent4) {
            parent4.classList.add("mm-show"); // ul
            const parent5 = parent4.parentElement;
            if (parent5) {
              parent5.classList.add("mm-show"); // li
              parent5.childNodes[0].classList.add("mm-active"); // a tag
            }
          }
        }
      }
      scrollElement(item);
      return false;
    }
    scrollElement(item);
    return false;
  }, []);

  const removeActivation = (items) => {
    for (var i = 0; i < items.length; ++i) {
      var item = items[i];
      const parent = items[i].parentElement;

      if (item && item.classList.contains("active")) {
        item.classList.remove("active");
      }
      if (parent) {
        const parent2El =
          parent.childNodes && parent.childNodes.lenght && parent.childNodes[1]
            ? parent.childNodes[1]
            : null;
        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show");
        }

        parent.classList.remove("mm-active");
        const parent2 = parent.parentElement;

        if (parent2) {
          parent2.classList.remove("mm-show");

          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.remove("mm-active"); // li
            parent3.childNodes[0].classList.remove("mm-active");

            const parent4 = parent3.parentElement; // ul
            if (parent4) {
              parent4.classList.remove("mm-show"); // ul
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.remove("mm-show"); // li
                parent5.childNodes[0].classList.remove("mm-active"); // a tag
              }
            }
          }
        }
      }
    }
  };

  const activeMenu = useCallback(() => {
    const pathName = props.router.location.pathname;
    const fullPath = pathName;
    let matchingMenuItem = null;
    const ul = document.getElementById("side-menu");
    const items = ul.getElementsByTagName("a");
    removeActivation(items);

    for (let i = 0; i < items.length; ++i) {
      if (fullPath === items[i].pathname) {
        matchingMenuItem = items[i];
        break;
      }
    }
    if (!matchingMenuItem) {
      for (let i = 0; i < items.length; ++i) {
        const mp = items[i].getAttribute('data-match-paths');
        if (mp && mp.split(',').includes(fullPath)) {
          matchingMenuItem = items[i];
          break;
        }
      }
    }
    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    }
  }, [props.router.location.pathname, activateParentDropdown]);

  useEffect(() => {
    ref.current.recalculate();
  }, []);

  useEffect(() => {
    // Re-init MetisMenu whenever the sidebar data swaps (e.g. switching between an org and Global View).
    // Otherwise the old click handlers reference stale DOM nodes and throw
    // `Cannot read properties of null (reading 'closest')`.
    const instance = new MetisMenu("#side-menu");
    activeMenu();
    return () => {
      try { instance.dispose?.(); } catch (_) { /* no-op */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSidebarData]);
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    activeMenu();
  }, [activeMenu]);

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  return (
    <React.Fragment>
      <SimpleBar ref={ref} className="sidebar-menu-scroll">
        {/* Org switcher card replaces the old "PAGES" header at the top of
            the sidebar. It's the primary surface for switching organizations
            (the topbar dropdown still works as a secondary entry point). */}
        <SidebarOrgSwitcher />
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            {(filteredSidebarData || []).map((item, key) => (
              <React.Fragment key={key}>
                {/* Skip the legacy "PAGES" main-menu header — the org
                    switcher above takes over that visual role. Other
                    isMainMenu entries (if any are added later) still
                    render as section labels. */}
                {item.isMainMenu && item.id === 'pages' ? null : item.isMainMenu ? (
                  <li className="menu-title">{item.label}</li>
                ) : (
                  <li key={key}>
                    <Link to={item.url ? item.url : "/#"}
                      id={`sidebar-item-${item.id || key}`}
                      className={item.url ? "" : "has-arrow"}
                      data-match-paths={item.matchPaths ? item.matchPaths.join(',') : undefined}
                    >
                      <i
                        className={item.icon}
                      ></i>
                      <span
                        className="menu-item"
                      >
                        {item.label}
                      </span>
                      {item.isLocked && (
                        <i
                          className="bx bxs-lock-alt ms-2"
                          style={{
                            fontSize: '12px',
                            color: '#878a99',
                            marginLeft: '8px'
                          }}
                        />
                      )}
                      {item.issubMenubadge && (
                        <span
                          className={
                            "badge rounded-pill " + item.bgcolor
                          }
                        >
                          {" "}
                          {props.t(item.badgeValue)}
                          {" "}
                        </span>
                      )}
                      {item.id === "service_requests" && pendingSRCount > 0 && (
                        <span
                          className="badge rounded-pill bg-warning text-white"
                          style={{ marginLeft: 'auto', fontSize: '10px' }}
                        >
                          {pendingSRCount}
                        </span>
                      )}
                      {item.id === "planning" && pendingPlanningCount > 0 && (
                        <span
                          className="badge rounded-pill bg-warning text-white"
                          style={{ marginLeft: 'auto', fontSize: '10px' }}
                        >
                          {pendingPlanningCount}
                        </span>
                      )}

                    </Link>
                    {isCollapsed && item.label && (
                      <UncontrolledTooltip
                        placement="right"
                        target={`sidebar-item-${item.id || key}`}
                      >
                        {item.label}
                      </UncontrolledTooltip>
                    )}
                    {item.subItem && (
                      <ul className="sub-menu">
                        {item.subItem.map((item, key) => (
                          <li key={key}>
                            <Link
                              to={item.link}
                              className={
                                item.subMenu && "has-arrow"
                              }
                            >
                              {item.sublabel}
                            </Link>
                            {item.subMenu && (
                              <ul className="sub-menu">
                                {item.subMenu.map((item, key) => (
                                  <li key={key}>
                                    <Link to="#">{props.t(item.title)}</Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                )}
              </React.Fragment>
            ))}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(SidebarContent));
